/**
 * v133.2 — Shared distance picker.
 *
 * Single source of truth for distance selection across the Range Log:
 *   - Live Session capture (ActiveSession.tsx)
 *   - Live Session begin modal (BeginSessionModal in RangeView.tsx)
 *   - Quick Log modal (LogSessionModal in RangeView.tsx)
 *
 * Behavior (locked from the v133.1 spec):
 *   - Primary chips 20/30/40/50/60/70 always visible (44px+ touch targets)
 *   - "+ More" toggle reveals 80/90/100/120/140
 *   - "Custom…" chip opens an inline numeric input (inputmode="decimal")
 *   - Recent distances row (last 5 used) below the chips,
 *     persisted to localStorage['sparrow.rangeLog.recentDistances'].
 *   - Hard cap: 200 yd (silently clamps)
 *   - Soft warn: > 150 yd triggers a confirm dialog
 *   - Active distance highlighted ember; current distance label
 *     visible at the top of the section
 */

import { useState } from 'react';
import clsx from 'clsx';

/** Primary preset chips — common hunting / 3D yardages. */
export const DISTANCE_PRESETS = [20, 30, 40, 50, 60, 70];
/** Extended chips revealed via the "More" toggle. Target / long-range archers. */
export const DISTANCE_PRESETS_EXTENDED = [80, 90, 100, 120, 140];
/** Hard cap to keep typos from logging 7000 yd shots. */
export const DISTANCE_MAX_YD = 200;
/** Soft warn threshold — confirm if user types above this. */
export const DISTANCE_WARN_YD = 150;
/** Storage key for the rolling recent-distances list. SHARED across surfaces. */
export const RECENT_DISTANCES_KEY = 'sparrow.rangeLog.recentDistances';
export const RECENT_DISTANCES_MAX = 5;

export function readRecentDistances(): number[] {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(RECENT_DISTANCES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0);
  } catch { return []; }
}
export function pushRecentDistance(d: number): number[] {
  if (!Number.isFinite(d) || d <= 0) return readRecentDistances();
  const current = readRecentDistances();
  const next = [d, ...current.filter((x) => x !== d)].slice(0, RECENT_DISTANCES_MAX);
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(RECENT_DISTANCES_KEY, JSON.stringify(next));
    }
  } catch { /* ignore */ }
  return next;
}

interface DistancePickerProps {
  /** Current distance value in yards. */
  value: number;
  /** Called when the user commits a new distance (chip tap, custom-input
   *  submit, or recent-row tap). NOT called for typing in the custom input. */
  onChange: (d: number) => void;
  /** Optional label displayed above the chip row. Defaults to "Distance". */
  label?: string;
  /** Show the "Recent" row below the chips. Default true. */
  showRecent?: boolean;
  /** Compact mode (smaller chips, no header label). Used in modals where
   *  vertical space matters. Default false. */
  compact?: boolean;
}

export function DistancePicker({
  value, onChange, label = 'Distance', showRecent = true, compact = false,
}: DistancePickerProps) {
  const [showExtended, setShowExtended] = useState<boolean>(value > 70);
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState<string>('');
  const [recents, setRecents] = useState<number[]>(() => readRecentDistances());

  const commit = (n: number) => {
    if (!Number.isFinite(n) || n <= 0) return;
    if (n > DISTANCE_MAX_YD) n = DISTANCE_MAX_YD;
    if (n > DISTANCE_WARN_YD) {
      if (!confirm(`${n} yards is unusually long — confirm this is what you mean?`)) return;
    }
    onChange(n);
    setRecents(pushRecentDistance(n));
    setCustomMode(false);
    setCustomText('');
  };

  const chipBase = compact
    ? 'px-2.5 py-1.5 rounded text-sm font-mono border min-h-[40px] min-w-[40px] touch-manipulation transition'
    : 'px-3 py-2 rounded text-sm font-mono border min-h-[44px] min-w-[44px] touch-manipulation transition';

  return (
    <div className="space-y-1.5">
      {!compact && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            {label} · <span className="text-ember-300">{value} yd</span>
          </span>
          <button
            type="button"
            onClick={() => setShowExtended((v) => !v)}
            className="text-[10px] font-mono uppercase tracking-wider text-cyan-400 hover:text-cyan-300 touch-manipulation"
          >
            {showExtended ? '− Less' : '+ More'}
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {DISTANCE_PRESETS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => commit(d)}
            className={clsx(chipBase,
              value === d
                ? 'bg-ember-500 text-zinc-950 border-ember-500'
                : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-600',
            )}
          >{d}</button>
        ))}
        {showExtended && DISTANCE_PRESETS_EXTENDED.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => commit(d)}
            className={clsx(chipBase,
              value === d
                ? 'bg-ember-500 text-zinc-950 border-ember-500'
                : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-600',
            )}
          >{d}</button>
        ))}
        <button
          type="button"
          onClick={() => { setCustomMode(true); setCustomText(String(value)); }}
          className={clsx(chipBase,
            'border-dashed border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/5',
          )}
        >Custom…</button>
        {compact && (
          <button
            type="button"
            onClick={() => setShowExtended((v) => !v)}
            className="text-[11px] font-mono uppercase tracking-wider text-cyan-400 hover:text-cyan-300 px-2 self-center"
          >
            {showExtended ? '− Less' : '+ More'}
          </button>
        )}
      </div>

      {customMode && (
        <div className="flex items-center gap-2 bg-zinc-900/40 border border-cyan-500/30 rounded-md px-2 py-2">
          <input
            type="number"
            inputMode="decimal"
            min={1}
            max={DISTANCE_MAX_YD}
            step={1}
            value={customText}
            autoFocus
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(parseFloat(customText)); }}
            className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm font-mono tabular-nums"
            placeholder="Yards (1–200)"
            aria-label="Custom distance in yards"
          />
          <button
            type="button"
            onClick={() => commit(parseFloat(customText))}
            className="px-3 py-2 min-h-[44px] bg-ember-500 text-zinc-950 rounded text-[12px] font-mono uppercase tracking-wider touch-manipulation"
            disabled={!customText.trim()}
          >Set</button>
          <button
            type="button"
            onClick={() => { setCustomMode(false); setCustomText(''); }}
            className="px-2 py-2 min-h-[44px] text-zinc-500 hover:text-zinc-200 text-[12px] font-mono uppercase tracking-wider touch-manipulation"
          >Cancel</button>
        </div>
      )}

      {showRecent && recents.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap pt-1">
          <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-600">Recent</span>
          {recents.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => commit(d)}
              className={clsx(
                'px-2 py-1 rounded text-[12px] font-mono border touch-manipulation',
                value === d
                  ? 'border-ember-500/60 text-ember-300 bg-ember-500/10'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-600',
              )}
            >{d}</button>
          ))}
        </div>
      )}
    </div>
  );
}
